import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import ServiceCentersTransferList from './ServiceCentersTransferList';
import {
  Alert,
  Box,
  Button,
  Card,
  CardHeader,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  FormGroup,
  Grid,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import { PartnerSafeApi } from './EnvFiles';
import { getStoredToken } from "../utils/getStoredToken";

const EditUser = ({ selectedUser }) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!selectedUser) {
      navigate('/users-panel/management')
    }
  }, [selectedUser, navigate]);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [roles, setRoles] = useState(null);
  const [serviceCenters, setServiceCenters] = useState(null);
  const [editedUser, setEditedUser] = useState({
    nome: selectedUser?.nome || '',
    ativo: selectedUser?.ativo || false,
    roles: selectedUser?.roles.map((role) => role.identificador) || [],
    serviceCenters: selectedUser?.centroDeServico?.map((cs) => cs.identificador)
      || [],
  });
  const [isAdmin, setIsAdmin] = useState(null);

  const fetchMyData = useCallback(async () => {
    const token = getStoredToken();

    try {
      const response = await axios.get(
        `${PartnerSafeApi}/api/v1/Contas/MeusDados`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = response.data;

      const currentIsAdmin = data.roles.some(
        (role) => ['Admin', 'BackOffice'].includes(role.nome),
      );

      if (currentIsAdmin) {
        setIsAdmin(currentIsAdmin)
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('Erro ao buscar dados do usuário:', err);
    }
  }, [navigate]);

  useEffect(() => {
    fetchMyData();
  }, [fetchMyData]);

  useEffect(() => {
    const fetchServiceCenters = async () => {
      try {
        const token = getStoredToken();
        const response = await axios.get(
          `${PartnerSafeApi}/api/v1/CentroDeServicos/ListarCentros`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        console.log('Centros de serviço recebidos:', response.data);

        // Formatando os dados para o formato esperado pelo componente
        const formattedCenters = response.data.map(center => ({
          id: center.identificador,
          des_centro_servico: center.descricao
        }));

        console.log('Centros de serviço formatados:', formattedCenters);
        setServiceCenters(formattedCenters);
      } catch (error) {
        console.error('Erro ao buscar centros de serviços:', error);
      }
    };
    fetchServiceCenters();
  }, []);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const token = getStoredToken();
        const response = await axios.get(`${PartnerSafeApi}/api/v1/Contas/ListarRoles`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setRoles(response.data);
      } catch (error) {
        console.error('Erro ao buscar roles:', error);
      }
    };
    fetchRoles();
  }, []);

  const isLoading = useMemo(() => (
    !isAdmin || serviceCenters === null || roles === null
  ), [isAdmin, serviceCenters, roles]);

  useEffect(() => console.log('editedUser', editedUser), [editedUser]);

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleCheckboxChange = (type, value) => {
    setEditedUser((previous) => {
      const currentValues = previous[type];
      const isChecked = currentValues.includes(value);

      return {
        ...previous,
        [type]: isChecked
          ? currentValues.filter((item) => item !== value)
          : [...currentValues, value],
      };
    });
  };

  const handleUserNameChange = (event) => {
    setEditedUser((previous) => ({
      ...previous,
      nome: event.target.value,
    }));
  };

  const handleActiveStatusChange = (event) => {
    setEditedUser((previous) => ({
      ...previous,
      ativo: event.target.checked,
    }));
  };

  const handleServiceCentersChange = (selectedCenters) => {
    console.log('Centros selecionados:', selectedCenters);
    setEditedUser(prev => ({
      ...prev,
      serviceCenters: selectedCenters.map(center => center.id)
    }));
  };

  const handleSubmit = async () => {
    const token = getStoredToken();
    const dataToSend = {
      usuarioIdentificador: selectedUser?.identificador,
      primeiroNome: editedUser.nome.split(' ')[0],
      sobrenome: editedUser.nome.split(' ').slice(1).join(' '),
      ativo: editedUser.ativo,
      centroDeServicoIdentificadores: editedUser.serviceCenters,
      roleIdentificadores: editedUser.roles,
    };

    console.log('EditUser :: handleSubmit: dataToSend', dataToSend);

    try {
      await axios.put(
        `${PartnerSafeApi}/api/v1/Contas/AtualizarDadosDoUsuario`,
        dataToSend,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSnackbarSeverity('success');
      setSnackbarMessage('Usuário atualizado com sucesso!');
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarSeverity('error');
      setSnackbarMessage('Erro ao atualizar o usuário!');
      setSnackbarOpen(true);

      console.error('Erro ao atualizar os dados do usuário:', error);
    }
  };

  const selectedCenters = useMemo(() => {
    return (serviceCenters || []).filter(center => 
      editedUser.serviceCenters.includes(center.id)
    );
  }, [serviceCenters, editedUser.serviceCenters]);

  // Log para debug
  console.log('Estado atual:', {
    serviceCenters,
    editedUser,
    selectedUser
  });

  if (isLoading || !selectedUser) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress
          size={60}
          sx={{
            color: 'primary.main',
            '& .MuiCircularProgress-circle': {
              strokeLinecap: 'round',
            },
          }}
        />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Card>
        <CardHeader
          title={
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
            }}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#40685E' }}>
                Gestão de Usuários: {selectedUser.nome || ''}
              </Typography>
              <Button
                variant="outlined"
                onClick={() => navigate('/users-panel/management')}
              >
                Voltar
              </Button>
            </Box>
          }
        />
        <Box sx={{ px: 3, pb: 2 }}>
          <Box sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}>
            <Box sx={{ width: '55%' }}>
              <TextField
                value={editedUser.nome}
                onChange={handleUserNameChange}
                label="Nome do Usuário"
                variant="outlined"
                fullWidth
                sx={{ width: '100%', maxWidth: '100%' }}
              />
            </Box>
            <Box sx={{ width: '45%' }}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editedUser.ativo}
                      onChange={handleActiveStatusChange}
                    />
                  }
                  label={editedUser.ativo ? "Usuário Ativo" : "Usuário Inativo"}
                  sx={{ ml: 2, whiteSpace: 'nowrap' }}
                />
              </FormGroup>
            </Box>
          </Box>

          <Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Permissões
              </Typography>
              <Grid container spacing={2}>
                {roles.map((role) => (
                  <Grid
                    key={role.identificador}
                    item
                    xs={12}
                    sm={6}
                    md={3}
                    lg={3}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editedUser.roles.includes(role.identificador)}
                          onChange={() => {
                            handleCheckboxChange('roles', role.identificador)
                          }}
                        />
                      }
                      label={role.nome}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
            <Box>
              <Box
                sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  minWidth: '100%',
                  mt: 2,
                }}
              >
                <Box sx={{ 
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  mb: 2
                }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Centros de Serviço
                  </Typography>
                </Box>
                <Box sx={{ 
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                }}>
                  {!!serviceCenters.length && (
                    <ServiceCentersTransferList
                      availableCenters={serviceCenters}
                      selectedCenters={selectedCenters}
                      onSelectionChange={handleServiceCentersChange}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
            >
              Salvar Alterações
            </Button>
          </Box>
        </Box>
      </Card>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default EditUser;
